import apiClient from './client';

export type InvoiceStatus = 'draft' | 'sent' | 'viewed' | 'partial' | 'paid' | 'overdue' | 'cancelled' | 'revised';

export interface Invoice {
  id: number;
  invoice_number: string;
  visible_to_client?: number | boolean;
  client_id: number;
  client_name?: string;
  client_company?: string;
  client_email?: string;
  client_phone?: string;
  client_address?: string;
  job_id?: number;
  job_title?: string;
  job_site_address?: string;
  quote_id?: number;
  quote_number?: string;
  status: InvoiceStatus;
  invoice_date: string;
  due_date: string;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total: number;
  amount_paid: number;
  balance_due: number;
  payment_terms: number;
  gst_rate?: number;
  pst_rate?: number;
  gst_amount?: number;
  pst_amount?: number;
  holdback_percent?: number;
  holdback_amount?: number;
  holdback_released_at?: string;
  notes?: string;
  parent_invoice_id?: number;
  revision_number: number;
  revision_notes?: string;
  created_at: string;
  updated_at: string;
}

export type InvoiceItemType = 'item' | 'discount';
export type DiscountType = 'fixed' | 'percent';

export interface InvoiceItem {
  id: number;
  invoice_id: number;
  description: string;
  item_type: InvoiceItemType;
  discount_type?: DiscountType;
  quantity: number;
  unit?: string;
  unit_price: number;
  total: number;
  display_order: number;
}

export interface InvoicePayment {
  id: number;
  invoice_id: number;
  amount: number;
  payment_date: string;
  payment_method?: string;
  reference_number?: string;
  notes?: string;
  source_invoice_number?: string;
  created_at: string;
}

export interface InvoiceRevision {
  id: number;
  invoice_number: string;
  revision_number: number;
  status: InvoiceStatus;
  total: number;
  created_at: string;
  revision_notes?: string;
}

export interface InvoiceAttachment {
  id: number;
  invoice_id: number;
  document_id: number | null;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  display_order: number;
  label: string | null;
  uploaded_by: number | null;
  url: string;
  created_at: string;
}

export interface InvoiceDetail extends Invoice {
  items: InvoiceItem[];
  payments: InvoicePayment[];
  attachments?: InvoiceAttachment[];
  revisions?: InvoiceRevision[];
}

export interface InvoicesListResponse {
  invoices: Invoice[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateInvoiceData {
  /** Job expenses billed through on this invoice (#210) */
  expense_ids?: number[];
  expense_billed?: Record<string, number>;
  client_id: number;
  job_id?: number;
  quote_id?: number;
  invoice_date?: string;
  due_date?: string;
  tax_rate?: number;
  gst_rate?: number;
  pst_rate?: number;
  gst_amount?: number;
  pst_amount?: number;
  holdback_percent?: number;
  holdback_amount?: number;
  notes?: string;
  items?: Array<{
    description: string;
    item_type?: InvoiceItemType;
    discount_type?: DiscountType;
    quantity: number;
    unit?: string;
    unit_price: number;
  }>;
}

export interface UpdateInvoiceData {
  status?: InvoiceStatus;
  invoice_date?: string;
  due_date?: string;
  tax_rate?: number;
  gst_rate?: number;
  pst_rate?: number;
  gst_amount?: number;
  pst_amount?: number;
  holdback_percent?: number;
  holdback_amount?: number;
  notes?: string;
  revision_notes?: string;
  items?: Array<{
    description: string;
    item_type?: InvoiceItemType;
    discount_type?: DiscountType;
    quantity: number;
    unit?: string;
    unit_price: number;
  }>;
}

export interface RecordPaymentData {
  amount: number;
  payment_date?: string;
  payment_method?: string;
  reference_number?: string;
  notes?: string;
}

// Get all invoices with filtering
export const getInvoices = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  client_id?: number;
  job_id?: number;
}): Promise<InvoicesListResponse> => {
  const response = await apiClient.get('/invoices', { params });
  return response.data.data;
};

// Get invoices for a specific client
export const getInvoicesByClient = async (clientId: number): Promise<Invoice[]> => {
  const response = await apiClient.get(`/invoices/client/${clientId}`);
  return response.data.data;
};

/**
 * Canonical nested path for a single invoice: clients › jobs › invoices.
 * Every "view this invoice" action across the app routes here so there is one
 * unified detail destination. Falls back to a client-scoped path when the
 * invoice isn't tied to a job.
 */
export function invoicePath(inv: { id: number; client_id: number; job_id?: number | null }): string {
  return inv.job_id
    ? `/clients/${inv.client_id}/jobs/${inv.job_id}/invoices/${inv.id}`
    : `/clients/${inv.client_id}/invoices/${inv.id}`;
}

// Get invoice by ID with items and payments
export const getInvoice = async (id: number): Promise<InvoiceDetail> => {
  const response = await apiClient.get(`/invoices/${id}`);
  return response.data.data;
};

// Create new invoice
export const createInvoice = async (data: CreateInvoiceData): Promise<{ id: number; invoiceNumber: string }> => {
  const response = await apiClient.post('/invoices', data);
  return response.data.data;
};

// Update invoice (may return revision data if a new revision was created)
export const updateInvoice = async (
  id: number,
  data: UpdateInvoiceData
): Promise<{ id?: number; invoiceNumber?: string; revisionNumber?: number } | void> => {
  const response = await apiClient.put(`/invoices/${id}`, data);
  return response.data.data;
};

// Delete invoice
export const deleteInvoice = async (id: number): Promise<void> => {
  await apiClient.delete(`/invoices/${id}`);
};

// Mark invoice as sent
export const sendInvoice = async (id: number, message?: string): Promise<{ emailed: boolean; emailTo?: string; emailReason?: string }> => {
  const response = await apiClient.post(`/invoices/${id}/send`, message ? { message } : {});
  return response.data || { emailed: false };
};

// Record a payment
export const recordPayment = async (
  invoiceId: number,
  data: RecordPaymentData
): Promise<{ amount_paid: number; balance_due: number; status: InvoiceStatus }> => {
  const response = await apiClient.post(`/invoices/${invoiceId}/payments`, data);
  return response.data.data;
};

// Get payment history
export const getPayments = async (invoiceId: number): Promise<InvoicePayment[]> => {
  const response = await apiClient.get(`/invoices/${invoiceId}/payments`);
  return response.data.data;
};

// Create invoice from quote
export const createInvoiceFromQuote = async (quoteId: number): Promise<{ id: number; invoiceNumber: string }> => {
  const response = await apiClient.post(`/invoices/from-quote/${quoteId}`);
  return response.data.data;
};

// ========== Invoice Attachments ==========

export const getInvoiceAttachments = async (invoiceId: number): Promise<InvoiceAttachment[]> => {
  const response = await apiClient.get(`/invoices/${invoiceId}/attachments`);
  return response.data.data;
};

export const uploadInvoiceAttachment = async (
  invoiceId: number,
  file: File,
  label?: string
): Promise<InvoiceAttachment> => {
  const formData = new FormData();
  formData.append('file', file);
  if (label) formData.append('label', label);
  const response = await apiClient.post(`/invoices/${invoiceId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.data;
};

export const linkDocumentToInvoice = async (
  invoiceId: number,
  documentId: number
): Promise<InvoiceAttachment> => {
  const response = await apiClient.post(`/invoices/${invoiceId}/attachments/from-document`, {
    document_id: documentId,
  });
  return response.data.data;
};

export const reorderInvoiceAttachments = async (
  invoiceId: number,
  order: Array<{ id: number; display_order: number }>
): Promise<void> => {
  await apiClient.put(`/invoices/${invoiceId}/attachments/reorder`, { order });
};

export const updateInvoiceAttachment = async (
  invoiceId: number,
  attachmentId: number,
  data: { label?: string }
): Promise<void> => {
  await apiClient.put(`/invoices/${invoiceId}/attachments/${attachmentId}`, data);
};

export const deleteInvoiceAttachment = async (
  invoiceId: number,
  attachmentId: number
): Promise<void> => {
  await apiClient.delete(`/invoices/${invoiceId}/attachments/${attachmentId}`);
};

// Release holdback — moves held-back amount back into collectible balance
export const releaseHoldback = async (id: number): Promise<void> => {
  await apiClient.post(`/invoices/${id}/release-holdback`);
};
